import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import './Profile.css';
import { adminApi } from '../services/api';
import { User } from '../types';

function Profile() {
  const { user, loading, refreshUser } = useAuth();
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState<'profile' | 'restore-users'>('profile');
  const [inactiveUsers, setInactiveUsers] = useState<User[]>([]);
  const [loadingInactive, setLoadingInactive] = useState(false);

  useEffect(() => {
    if (user) setFullName(user.full_name || '');
  }, [user]);

  const isSuperAdmin = useMemo(
    () => Boolean(user && (user.role === 'superadmin' || user.is_superuser)),
    [user]
  );

  const loadInactiveUsers = useCallback(async () => {
    if (!isSuperAdmin) return;
    setLoadingInactive(true);
    try {
      const allUsers = await adminApi.listUsers(0, 1000, true);
      // Super admins can restore users across all organizations
      const inactive = allUsers.filter(u => !u.is_active);
      setInactiveUsers(inactive);
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || 'Failed to load inactive users');
    } finally {
      setLoadingInactive(false);
    }
  }, [isSuperAdmin]);

  const handleRestoreUser = useCallback(async (id: number) => {
    try {
      await adminApi.restoreUser(id);
      toast.success('User restored');
      await loadInactiveUsers();
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || 'Failed to restore user');
    }
  }, [loadInactiveUsers]);

  useEffect(() => {
    if (activeSection === 'restore-users' && isSuperAdmin) {
      loadInactiveUsers();
    }
  }, [activeSection, isSuperAdmin, loadInactiveUsers]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/v1/auth/me', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify({ full_name: fullName, password: password || undefined }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to update profile');
      }

      // Refresh user in AuthContext so navbar and app reflect changes
      try {
        await refreshUser();
      } catch (err) {
        // Non-fatal: still consider profile updated
        console.warn('Failed to refresh user context', err);
      }

      toast.success('Profile updated');
      setPassword('');
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const renderProfileForm = () => (
    <div className="profile-card card">
      <h2>Account Details</h2>
      <form onSubmit={handleSave} className="profile-form">
        <div className="form-row">
          <label>Email</label>
          <input value={user?.email || ''} disabled />
        </div>

        <div className="form-row">
          <label>Full Name</label>
          <input value={fullName} onChange={e => setFullName(e.target.value)} />
        </div>

        <div className="form-row">
          <label>New Password (leave blank to keep current)</label>
          <input type="password" value={password} onChange={e => setPassword(e.target.value)} />
        </div>

        <div className="form-row">
          <label>Role</label>
          <input value={user?.role || 'user'} disabled />
        </div>

        <div className="form-row">
          <label>Organization</label>
          <input value={user?.organization_id ? `ID: ${user.organization_id}` : 'None'} disabled />
        </div>

        <div className="form-actions">
          <button className="btn btn-primary" type="submit" disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );

  const renderRestoreUsersSection = () => (
    <div className="restore-users-section card">
      <h2>Restore Users</h2>
      <p className="text-muted">Restore previously disabled users across all organizations.</p>
      {loadingInactive ? (
        <div className="loading">Loading users...</div>
      ) : inactiveUsers.length === 0 ? (
        <div className="empty-state">
          <p className="text-muted">No inactive users found.</p>
        </div>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Role</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {inactiveUsers.map(u => (
                <tr key={u.id}>
                  <td>{u.email}</td>
                  <td>{u.full_name || '-'}</td>
                  <td>{u.role}</td>
                  <td>
                    <button
                      className="btn btn-sm btn-primary"
                      type="button"
                      onClick={() => handleRestoreUser(u.id)}
                    >
                      Restore
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="profile-page">
      <div className="page-header">
        <h1>Profile</h1>
        <p className="page-subtitle">Manage your account settings</p>
      </div>

      {isSuperAdmin ? (
        <div className="profile-layout">
          <aside className="profile-menu">
            <button
              className={`profile-menu-item ${activeSection === 'profile' ? 'active' : ''}`}
              onClick={() => setActiveSection('profile')}
            >
              Account Details
            </button>
            <button
              className={`profile-menu-item ${activeSection === 'restore-users' ? 'active' : ''}`}
              onClick={() => setActiveSection('restore-users')}
            >
              Restore Users
            </button>
          </aside>
          <section className="profile-content">
            {activeSection === 'profile' ? renderProfileForm() : renderRestoreUsersSection()}
          </section>
        </div>
      ) : (
        renderProfileForm()
      )}
    </div>
  );
}

export default Profile;
